import { supabase } from "@/integrations/supabase/client";

// Module-level singleton to prevent overlapping audio across all callers
let currentAudio: HTMLAudioElement | null = null;

export function stopCurrentAudio() {
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
}

export async function playAIResponse(
  text: string,
  audioRef: React.MutableRefObject<HTMLAudioElement | null>,
  setIsPlaying: (playing: boolean) => void
): Promise<void> {
  // Stop any globally playing audio (from any caller)
  stopCurrentAudio();

  // Stop caller's own audio ref if different
  if (audioRef.current) {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    audioRef.current = null;
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-tts`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text }),
    }
  );

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`TTS request failed (${response.status}): ${errText}`);
  }

  // Response is now JSON with audioUrl (or fallback raw audio)
  const contentType = response.headers.get("Content-Type") || "";

  let audioUrl: string;
  let isObjectUrl = false;

  if (contentType.includes("application/json")) {
    const data = await response.json();
    audioUrl = data.audioUrl;
  } else {
    // Fallback: raw audio blob (if storage upload failed)
    const audioBlob = await response.blob();
    audioUrl = URL.createObjectURL(audioBlob);
    isObjectUrl = true;
  }

  const audio = new Audio(audioUrl);
  audioRef.current = audio;
  currentAudio = audio;
  setIsPlaying(true);

  const cleanup = () => {
    setIsPlaying(false);
    if (isObjectUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    if (audioRef.current === audio) {
      audioRef.current = null;
    }
    if (currentAudio === audio) {
      currentAudio = null;
    }
  };

  audio.onended = cleanup;
  audio.onerror = cleanup;

  await audio.play();
}
